import {
  PhilatelicItemType,
  type SingleStamp,
  type WithoutIdAndReview,
  type ReviewState,
  type GumType,
  type StampColor,
  type CatalogIds,
  type Zhihao,
} from "@briefmarken_katalog/types";

import { StampBaseModel } from "./StampBaseModel.svelte";
import { validateZhihaoField } from "./validateZhihaoField";
import type { TranslationKey } from "$lib/i18n";

export class StampEditorModel extends StampBaseModel {
  date_of_issue = $state<Date | null>(null);
  expiry_date = $state<Date | null>(null);
  print_run = $state<number | null>(null);
  categories = $state<string[]>([]);
  catalog_ids = $state<CatalogIds>({});
  /**
   * A single stamp carries no set notation: the position within an issue is a property of
   * the set, not of the stamp, and is assigned in the set editor.
   */
  zhihao = $state<Zhihao | undefined>(undefined);
  isDateInvalid = $derived(this.date_of_issue === null);
  isReview = $state(false);
  isDelete = $state(false);
  isEdit = $state(false);
  with_gum = $state<GumType | undefined>(undefined);
  color = $state<StampColor | undefined>({ isMultiColored: true });
  dialogTitle = $state<TranslationKey>('editor.stampEditorTitle');
  dialogSubtitle = $state<TranslationKey>('editor.stampEditorSubtitle');
  dialogSubtitleSuffix = $state('');
  showSubmitButton = $state(false);
  private initialSnapshot: string | null = null;
  isDirty = $derived.by(() => {
    if (this.initialSnapshot === null) return true;
    return this.pictureDirty || JSON.stringify(this.buildSnapshot()) !== this.initialSnapshot;
  });

  onCreate: (stamp: WithoutIdAndReview<SingleStamp>) => void;
  onReview: (stamp: WithoutIdAndReview<SingleStamp>, state: ReviewState) => void;
  onDelete: () => void;

  constructor({ onCreate = (stamp: WithoutIdAndReview<SingleStamp>) => {},
                onReview = (stamp: WithoutIdAndReview<SingleStamp>, state: ReviewState) => {},
                onDelete = () => {},
                isReadonly = false,
                isReview = false,
                isDelete = false,
                isEdit = false,
                initialValue = null,
                dialogTitle = 'editor.stampEditorTitle' as TranslationKey,
                dialogSubtitle = 'editor.stampEditorSubtitle' as TranslationKey,
              } :
              {
                onCreate?: (stamp: WithoutIdAndReview<SingleStamp>) => void,
                onReview?: (stamp: WithoutIdAndReview<SingleStamp>, state: ReviewState) => void,
                onDelete?: () => void,
                isReadonly?: boolean,
                isReview?: boolean,
                isDelete?: boolean,
                isEdit?: boolean,
                initialValue?: SingleStamp | null,
                dialogTitle?: TranslationKey,
                dialogSubtitle?: TranslationKey,
              })
  {
    super();
    this.onCreate = onCreate;
    this.onReview = onReview;
    this.onDelete = onDelete;
    this.isReadonly = isDelete ? true : isReadonly;
    this.isReview = isReview;
    this.isDelete = isDelete;
    this.isEdit = isEdit;
    this.dialogTitle = dialogTitle;
    this.dialogSubtitle = dialogSubtitle;
    this.showSubmitButton = initialValue === null || isEdit;

    if (initialValue)
    {
      this.title = initialValue.title;
      this.description = initialValue.description;
      this.date_of_issue = initialValue.date_of_issue;
      this.expiry_date = initialValue.expiry_date ?? null;
      this.print_run = initialValue.print_run ?? null;
      this.categories = initialValue.categories ?? [];
      this.catalog_ids = initialValue.catalog_ids ?? {};
      this.zhihao = initialValue.zhihao;
      this.face_value_unit = initialValue.face_value.unit;
      this.face_value_value = initialValue.face_value.value;
      this.picture = initialValue.pictures?.[0];
      this.loadPerforation(initialValue.perforation);
      this.with_gum = initialValue.with_gum;
      this.color = initialValue.color;
      this.initialSnapshot = JSON.stringify(this.buildSnapshot());
    }
  }

  private buildSnapshot() {
    return {
      title: this.title,
      description: this.description,
      date_of_issue: this.date_of_issue,
      expiry_date: this.expiry_date,
      print_run: this.print_run,
      categories: this.categories,
      catalog_ids: this.catalog_ids,
      zhihao: this.zhihao,
      face_value_value: this.face_value_value,
      face_value_unit: this.face_value_unit,
      ...this.perforationSnapshot(),
      with_gum: this.with_gum,
      color: this.color,
    };
  }

  validate(): [boolean, TranslationKey | '']
  {
    if (this.title === "")
    {
      return [false, 'validation.titleEmpty'];
    }
    if (this.date_of_issue === null)
    {
      return [false, 'validation.dateNotSet'];
    }
    return validateZhihaoField(this.zhihao) ?? this.validateCommon();
  }

  private buildStamp(): WithoutIdAndReview<SingleStamp> {
    return {
      title: this.title,
      description: this.description,
      date_of_issue: this.date_of_issue!,
      expiry_date: this.expiry_date ?? undefined,
      print_run: this.print_run ?? undefined,
      categories: this.categories.length > 0 ? this.categories : undefined,
      catalog_ids: Object.keys(this.catalog_ids).length > 0 ? this.catalog_ids : undefined,
      zhihao: this.zhihao,
      face_value: {
        value: this.face_value_value,
        unit: this.face_value_unit!,
      },
      pictures: this.picture ? [this.picture] : [],
      perforation: this.buildPerforation(),
      with_gum: this.with_gum,
      color: this.color,
      type: PhilatelicItemType.SingleStamp,
    };
  }

  onsubmit(close: () => void, ev: Event) {
    ev.preventDefault();

    if (!this.isValid)
    {
      return;
    }

    close();
    this.onCreate(this.buildStamp());
    this.reset();
  }

  onreview(state: ReviewState, close: () => void) {
    close();
    this.onReview(this.buildStamp(), state);
  }

  ondelete(close: () => void) {
    close();
    this.onDelete();
  }

  reset() {
    this.title = "";
    this.description = "";
    this.date_of_issue = null;
    this.expiry_date = null;
    this.print_run = null;
    this.categories = [];
    this.catalog_ids = {};
    this.zhihao = undefined;

    this.face_value_value = 0;
    this.face_value_unit = undefined;

    this.picture = undefined;

    this.resetPerforation();

    this.with_gum = undefined;
    this.color = { isMultiColored: true };
  }
}
